import { mapMutations } from 'vuex'

import auth from '../services/auth'

/**
 * ===============================================
 * HTTP Exception Mixin
 * ===============================================
 *
 * Handles HTTP exceptions by prompting users.
 * Imported by components that uses APIs, and
 * called in the .catch() fallback of HTTP calls.
 *
 * ===============================================
 */

export default {
  data () {
    return {
      error: null
    }
  },
  methods: {
    ...mapMutations('dialog', ['openAlertDialog']),

    /**
     * Handles critical error without response.
     *
     */
    criticalError () {
      this.openAlertDialog({
        title: `Server Error`,
        body: `We have encountered issues with our server, and our team is working on it. Apologies for any inconvenience this may have caused. Please check back soon.`
      })
    },

    /**
     * Determine the appropriate action for the error
     *
     * @param error
     */
    handle (error) {
      if (!error) return this.criticalError()

      this.setError(error)

      switch (error.status) {
        case 401:
          return this.unauthenticated()

        case 403:
          return this.unauthorized()

        case 404:
          return this.notFound()

        case 422:
          return this.unprocessable()

        case 500:
          return this.internalServerError()

        default:
          return this.defaultError()
      }
    },

    /**
     * Determine the appropriate action for the error
     *
     * @param error
     */
    handleAutomatedCycleError (error) {
      if (!error) return this.criticalError()

      const body = error.data.message

      this.openAlertDialog({
        title: error.statusText,
        body
      })
    },

    /**
     * Determine the appropriate action for the error
     *
     * @param error
     */
    handleErrorMessage (error, message) {
      if (!error) return this.criticalError()

      const body = message

      this.openAlertDialog({
        title: error.statusText,
        body
      })
    },

    /**
     * Parse error string if error is not yet an object
     *
     * @param error
     */
    setError (error) {
      if (typeof error !== 'object') error.error = JSON.parse(error.error)
      this.error = error
    },

    /**
     * Shows an alert dialog with the error status from response.
     *
     */
    defaultError () {
      this.openAlertDialog({
        title: this.error.statusText,
        body: this.error.message
      })
    },

    /**
     * Shows an alert dialog with a status of 500.
     *
     */
    internalServerError () {
      const body = 'Oops! Something went wrong. Please try again or refresh the page.'

      this.openAlertDialog({
        title: this.error.statusText,
        body
      })
    },

    /**
     * Shows an alert dialog with a status of 404.
     *
     */
    notFound () {
      const body = 'The requested resource was not found.'

      this.openAlertDialog({
        title: this.error.statusText,
        body
      })
    },

    /**
     * Shows an alert dialog with a status of 401.
     *
     */
    unauthenticated () {
      const body = 'Please login to continue.'

      this.openAlertDialog({
        title: this.error.statusText,
        body,
        action: () => auth.flush()
      })
    },

    /**
     * Shows an alert dialog with a status of 403.
     *
     */
    unauthorized () {
      const body = 'This action is unauthorized.'

      this.openAlertDialog({
        title: this.error.statusText,
        body
      })
    },

    /**
     * Shows an alert dialog with a status of 422.
     *
     */
    unprocessable () {
      const body = 'Please check the form for errors.'

      this.openAlertDialog({
        title: this.error.statusText,
        body
      })
    }
  }
}
